import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
   Row,
  Typography,
  Avatar, Button, Flex, Space, Table, Tag, Tooltip
} from "antd";
import Title from "antd/es/typography/Title";
import {
  DeleteIcon,
  EditIcon,
  InfoButtonIcon,
} from "../../../../../../assets/svg";
import { colors } from "../../../../../../constants/colors";
import {
  baseRouteMaker,
  routes,
} from "../../../../../../constants/routeConsts";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { TagOutlined } from "@ant-design/icons";
import { dateObjToDispViewDate } from "../../../../../../utils/utils";
import { offerByID } from "../../../../../../redux/reducers/offerReducer";
import { getOffers } from "../../../../../../redux/services/offer/offerServices";
import OfferDeleteDialog from "./OfferDeleteDialog";
import OfferSponsorshipFormDialog from "./OfferSponsorshipFormDialog";
import Actions from "../../../../../../redux/actions";
import useDebounce from "../../../../../../hooks/useDebounce";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { checkSponsorshipAction } from "../../../../../../redux/services/common/commonServices";
import useLocalStorage from "../../../../../../hooks/useLocalStorage";
import { appConfig } from "../../../../../../constants/config/appConfig";
import { getUserProfileAction } from "../../../../../../redux/services/auth/authServices";
import { UserOutlined } from "@ant-design/icons";
import "./Offer.module.scss";

const OfferList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { offerList } = useSelector((state) => state?.offers);
  const [openOfferDeleteDialog, setOpenOfferDeleteDialog] = useState(false);
  const [openSponsorshipDialog, setOpenSponsorshipDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const { userSession, setUserSession } = useLocalStorage(
    appConfig.SESSION_USER_INFO
  );
  const { sponsorshipAvailability } = useSelector((state) => state?.common);
  const { loginDetails } = useSelector((state) => state?.auth);

  const [searchValue] = useState("");
  const { debouncedValue: debouncedSearchValue } = useDebounce(
    searchValue,
    500
  );
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: offerList?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };

  useEffect(() => {
    userSession &&
      Object.keys(userSession).length !== 0 &&
      userSession !== null &&
      userSession !== undefined &&
      dispatch(getUserProfileAction(setUserSession)).then((apiRes) => {
        const dataToBeSent = {
          type: "offer",
          state: apiRes?.organization_id?.address?.state?.name,
          city: apiRes?.organization_id?.address?.city?.name,
        };
        dispatch(checkSponsorshipAction(dataToBeSent, setLoading));
      });
  }, []);

  useEffect(() => {
    handleGetTableData();
  }, [pagination, debouncedSearchValue]);

  const handleGetTableData = () => {
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
    };
    dispatch(getOffers(setLoading, dataToBeSent));
  };

  // offer -> table column data
  const offerColumnHeaderData = [
    {
      title: t("Modules.Offer.OfferForm.offerNameLabel"),
      key: "banner",
      render: (_, { banner }) => {
        return (
          <Flex gap="1rem" align="center">
            <Avatar src={banner} key={banner} icon={<UserOutlined />} />
            {`${_.title}`}
          </Flex>
        );
      },
    },
    {
      title: t("Modules.Offer.OfferForm.startDate"),
      dataIndex: "offerStartDate",
      key: "offerStartDate",
      render: (_, record) => (
        <p>{dateObjToDispViewDate(record?.offerStartDate, "ddmmyyyy")}</p>
      ),
    },
    {
      title: t("Modules.Offer.OfferForm.endDate"),
      dataIndex: "offerEndDate",
      key: "offerEndDate",
      render: (_, record) => (
        <p>{dateObjToDispViewDate(record?.offerEndDate, "ddmmyyyy")}</p>
      ),
    },
    {
      title: t("Modules.Offer.OfferForm.thSponsored"),
      key: "top_listing_enabled",
      dataIndex: "top_listing_enabled",
      render: (_, record) => (
        <>
          <Tag
            bordered={false}
            style={{
              background: record?.top_listing_enabled
                ? colors.lightTeal
                : colors.lightPinkishRed,
              color: record?.top_listing_enabled
                ? colors.darkTeal
                : colors.darkMaroon,
            }}
          >
            {record?.top_listing_enabled
              ? t("Modules.Offer.OfferForm.sponsored")
              : t("Modules.Offer.OfferForm.regular")}
          </Tag>
        </>
      ),
    },
    {
      title: (
        <>
          <Flex gap="1rem" align="center" justify="center">
            <>{t("Modules.Offer.OfferForm.addSponsor")}</>
            {loginDetails?.organization_id?.current_plan_id?.code ===
              "FREE" && (
              <Tooltip
                placement="topLeft"
                title={t("CommonUtils.upgradeYourSubscription")}
                color={colors.DarkMint}
              >
                <Button type="text" shape="circle" size="small">
                  <Flex align="center" justify="center">
                    <InfoButtonIcon />
                  </Flex>
                </Button>
              </Tooltip>
            )}
          </Flex>
        </>
      ),
      key: "purchase-organization-sponsorship",
      dataIndex: "purchase-organization-sponsorship",
      align: "center",
      render: (_, record) => (
        <>
          <Button
            type="primary"
            size="small"
            onClick={(e) => handleAddSponsorship(e, record)}
            disabled={
              !sponsorshipAvailability?.sposorship_available ||
              loginDetails?.organization_id?.current_plan_id?.code === "FREE" ||
              record?.top_listing_enabled
            }
          >
            {t("Modules.Offer.OfferForm.addSponsor")}
          </Button>
        </>
      ),
    },
    {
      title: t("Modules.AutoCredit.status"),
      key: "action",
      align: "center",
      render: (_, record) => {
        const getColorBasedOnStatus = (status) => {
          switch (status) {
            case true:
              return { background: colors.lightTeal, color: colors.darkTeal };

            case false:
              return {
                background: colors.lightPinkishRed,
                color: colors.darkMaroon,
              };

            default:
              break;
          }
        };
        return (
          <>
            <Tag
              bordered={false}
              style={{
                ...getColorBasedOnStatus(record?.is_active),
                textTransform: "capitalize",
              }}
            >
              {record?.is_active === true ? t("CommonUtils.active") : t("CommonUtils.inactive")}
            </Tag>
          </>
        );
      },
    },
    {
      title: t("Modules.Offer.OfferForm.action"),
      key: "action",
      align: "center",
      render: (_, record) => {
        return (
          <>
          {
            record?.is_active ?
            <Flex gap="small" justify="center">
              <Space size="middle">
                <Button
                  type="text"
                  size="medium"
                  icon={<EditIcon fillColor={colors.slateGray} />}
                  onClick={(e) => handleAddEditDetails(e, record)}
                />
              </Space>
              <Space size="middle">
                <Button
                  type="text"
                  size="medium"
                  icon={<DeleteIcon fillColor={colors.slateGray} />}
                  onClick={(e) => handleConfirmDelete(e, record)}
                />
              </Space>
            </Flex>
            :
            <>-</>
          }
          </>
        );
      },
    },
  ];

  const handleAddEditDetails = (e, row) => {
    dispatch(offerByID(""));
    navigate(baseRouteMaker(routes.CREATE_OFFER));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedOfferId = row?._id;

      dispatch(offerByID(selectedOfferId));
    }
  };

  const handleConfirmDelete = (e, row) => {
    setOpenOfferDeleteDialog(true);
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedOfferId = row?._id;
      dispatch(offerByID(selectedOfferId));
    }
  };

  const handleAddSponsorship = (e, row) => {
    dispatch(offerByID(""));
    setOpenSponsorshipDialog(true);
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedOfferId = row?._id;
      dispatch(offerByID(selectedOfferId));
    }
  };

  const isOrgVerified = useSelector(
    (state) => state?.auth?.loginDetails?.organization_id?.organization_verified
  );

  const openVerificationDialog = () => {
    dispatch(Actions.createAction(Actions.VERIFIED_DIALOG_OPEN));
  };

  return (
    <>
      <div className="offerListContainer">
        <Flex vertical gap="2rem">
          <Flex
            align="center"
            justify="space-between"
            style={{ padding: "10px" }}
          >
             <Card
                  bordered
                  hoverable
                  style={{
                    width: 300,
                    padding: 0,
                    borderRadius: 20,
                    border: `1px solid ${colors.oceanBlue}`,
                  }}
                >
              <Row gutter={[24, 24]} align="middle" justify="center">
                <Col span={6}>
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<TagOutlined style={{ fontSize: 22 }} />}
                    style={{ background: colors.oceanBlue }}
                  />
                </Col>
                <Col span={18}>
                  <Flex vertical>
                    <Typography.Title level={3}>
                      {t("Modules.Offer.OfferForm.title")}
                    </Typography.Title>
                    <Typography.Title
                      level={2}
                      style={{ margin: 0, color: colors.DarkMint }}
                    >
                      {`${offerList?.total || 0}`}
                    </Typography.Title>
                  </Flex>
                </Col>
              </Row>
            </Card>
            <Button
              type="primary"
              onClick={(e) =>
                isOrgVerified
                  ? handleAddEditDetails(e, null)
                  : openVerificationDialog()
              }
            >
              {t("Modules.Offer.OfferForm.addOffer")}
            </Button>
          </Flex>

          <Table
            dataSource={offerList.data}
            columns={offerColumnHeaderData}
            pagination={paginationObj}
          />
        </Flex>
        <OfferDeleteDialog
          open={openOfferDeleteDialog}
          onClose={() => {
            setOpenOfferDeleteDialog(false);
            handleGetTableData();
          }}
        />
        <OfferSponsorshipFormDialog
          open={openSponsorshipDialog}
          onClose={() => {
            setOpenSponsorshipDialog(false);
            handleGetTableData();
          }}
        />
      </div>
    </>
  );
};

export default OfferList;
